import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { useAuth } from '../context/AuthContext';
import { exportRowsToExcel } from '../utils/exportExcel';
import './VarianceReport.css';

const formatDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const RecallReport = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, user, isAdmin } = useAuth();
  const [records, setRecords] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLocations = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await fetchWithAuth('/api/consignment-locations');
      if (res.ok) {
        const list = await res.json();
        setLocations(Array.isArray(list) ? list : []);
      }
    } catch {
      setLocations([]);
    }
  }, [fetchWithAuth, isAdmin]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const loadRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      let url = '/api/reports/recall-detail';
      if (isAdmin && selectedLocationId !== '') {
        url += `?consignment_location_id=${encodeURIComponent(selectedLocationId)}`;
      }
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Failed to load recall report');
      const data = await response.json();
      setRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load recall report');
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, [fetchWithAuth, isAdmin, selectedLocationId]);

  useEffect(() => {
    loadRecords();
  }, [loadRecords]);

  const handleExport = () => {
    const stamp = new Date().toISOString().slice(0, 10);
    const rows = records.map((row) => {
      const fulfilled = row.fulfilledQuantity != null ? Number(row.fulfilledQuantity) : null;
      const expected = Number(row.expectedQuantity) || 0;
      const variance = fulfilled != null ? fulfilled - expected : '';
      return {
        'Transaction ID': row.transactionId || '',
        'Part number': row.partNumber || '',
        Description: row.partDescription || '',
        Expected: String(expected),
        Fulfilled: fulfilled != null ? String(fulfilled) : '',
        Variance: variance === '' ? '' : String(variance),
        Status: row.status || '',
        'Fulfilled at': row.fulfilledAt ? new Date(row.fulfilledAt).toLocaleString() : '',
        'Dispatched at': row.dispatchedAt ? new Date(row.dispatchedAt).toLocaleString() : '',
      };
    });
    exportRowsToExcel(`recall-report-${stamp}`, 'Recall', rows);
  };

  return (
    <Layout>
      <div className="variance-container">
        <div className="variance-header">
          <div className="variance-header-left">
            <button type="button" className="variance-back" onClick={() => navigate('/reports')}>
              ← Back to Reports
            </button>
            <h1>Recall Report</h1>
          </div>
          <div className="variance-header-actions">
            {isAdmin && (
              <label className="variance-filter">
                <span className="variance-filter-label">Location:</span>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  className="variance-filter-select"
                >
                  <option value="">All locations</option>
                  {locations.map((loc) => (
                    <option key={loc.id} value={loc.id}>
                      {loc.name || loc.code || `Location #${loc.id}`}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {!isAdmin && user?.consignmentLocationName && (
              <span className="variance-badge">Location: {user.consignmentLocationName}</span>
            )}
            <button type="button" className="variance-refresh" onClick={loadRecords}>
              Refresh
            </button>
            <button type="button" className="variance-refresh" onClick={handleExport} disabled={records.length === 0}>
              ⬇ Excel
            </button>
          </div>
        </div>
        <p className="variance-subtitle">
          Recall items from Excel import and fulfillment from mobile (expected vs fulfilled qty).
        </p>
        {error && <div className="variance-error">{error}</div>}
        {isLoading ? (
          <div className="variance-loading">Loading...</div>
        ) : records.length === 0 ? (
          <div className="variance-empty">
            <div className="variance-empty-icon">↩️</div>
            <h2>No recall records</h2>
            <p>Import a Recall Excel in Recall Import, then operators fulfill on mobile. Records appear here.</p>
          </div>
        ) : (
          <div className="variance-table-wrap">
            <table className="variance-table">
              <thead>
                <tr>
                  <th>Transaction ID</th>
                  <th>Part Number</th>
                  <th>Description</th>
                  <th>Expected</th>
                  <th>Fulfilled</th>
                  <th>Variance</th>
                  <th>Status</th>
                  <th>Fulfilled At</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => {
                  const fulfilled = row.fulfilledQuantity != null ? Number(row.fulfilledQuantity) : null;
                  const expected = Number(row.expectedQuantity) || 0;
                  const variance = fulfilled != null ? fulfilled - expected : null;
                  return (
                    <tr key={row.id}>
                      <td>{row.transactionId || '—'}</td>
                      <td>{row.partNumber || '—'}</td>
                      <td className="variance-desc">{(row.partDescription || '—').slice(0, 40)}{(row.partDescription || '').length > 40 ? '…' : ''}</td>
                      <td className="variance-num">{expected}</td>
                      <td className="variance-num">{fulfilled != null ? fulfilled : '—'}</td>
                      <td>
                        {variance != null ? (
                          <span className={variance >= 0 ? 'variance-over' : 'variance-short'}>
                            {variance >= 0 ? '+' : ''}{variance}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <span className={`variance-type variance-type-${(row.status || '').toLowerCase()}`}>
                          {(row.status || 'pending').charAt(0).toUpperCase() + (row.status || '').slice(1)}
                        </span>
                      </td>
                      <td className="variance-date">{formatDate(row.fulfilledAt || row.dispatchedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RecallReport;
